/**
 * useXRCapabilities — v10.9.0
 * Hook que detecta capacidades del dispositivo actual antes de instanciar
 * el adaptador. Consulta:
 *   - camera_supported (mediaDevices.getUserMedia)
 *   - marker_tracking_supported (camera + zxing lib presente)
 *   - webxr_supported (navigator.xr.isSessionSupported('immersive-ar'))
 *   - depth_supported / spatial_anchor_supported (siempre false en web)
 *   - is_standalone (PWA instalada)
 *   - is_online
 */
import { useEffect, useState } from 'react';

export default function useXRCapabilities() {
  const [caps, setCaps] = useState({
    loading: true,
    camera_supported: false,
    marker_tracking_supported: false,
    webxr_supported: false,
    depth_supported: false,
    spatial_anchor_supported: false,
    hand_tracking_supported: false,
    voice_supported: false,
    is_standalone: false,
    is_online: navigator.onLine,
    is_mobile: false,
    is_secure_context: window.isSecureContext,
  });

  useEffect(() => {
    let cancelled = false;
    const detect = async () => {
      const out = {
        loading: false,
        camera_supported: false,
        marker_tracking_supported: false,
        webxr_supported: false,
        depth_supported: false,
        spatial_anchor_supported: false,
        hand_tracking_supported: false,
        voice_supported: false,
        is_standalone: window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true,
        is_online: navigator.onLine,
        is_mobile: /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
        is_secure_context: window.isSecureContext,
      };
      if (navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') {
        out.camera_supported = true;
        out.marker_tracking_supported = true;
      }
      if (typeof navigator.xr !== 'undefined' && navigator.xr && typeof navigator.xr.isSessionSupported === 'function') {
        try { out.webxr_supported = !!(await navigator.xr.isSessionSupported('immersive-ar')); } catch { /* noop */ }
      }
      out.voice_supported = 'speechSynthesis' in window || 'webkitSpeechRecognition' in window;
      if (!cancelled) setCaps(out);
    };
    detect();
    const onOnline = () => setCaps((p) => ({ ...p, is_online: true }));
    const onOffline = () => setCaps((p) => ({ ...p, is_online: false }));
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { cancelled = true; window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  return caps;
}
